import { useEffect, useRef } from "react";
import Icon from "../Icon";
import "./index.scss";

const Modal = ({
  isOpen = false,
  onClose,
  children,
  className = "",
  showCloseIcon = true,
  closeOnOutsideClick = true,
  size = "medium",
  title,
  ...restProps
}) => {
  const modalRef = useRef(null);
  const overlayRef = useRef(null);

  // Handle escape key press
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && isOpen && onClose) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Handle outside click
  const handleOverlayClick = (event) => {
    if (
      closeOnOutsideClick &&
      onClose &&
      overlayRef.current &&
      event.target === overlayRef.current
    ) {
      onClose();
    }
  };

  // Handle close button click
  const handleCloseClick = () => {
    if (onClose) {
      onClose();
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  // Generate modal classes
  const modalClasses = ["modal", `modal--${size}`, className]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined}
    >
      <div
        ref={modalRef}
        className={modalClasses}
        onClick={(e) => e.stopPropagation()}
        {...restProps}
      >
        {/* Close button */}
        {showCloseIcon && (
          <button
            className="modal__close-btn"
            onClick={handleCloseClick}
            aria-label="Close modal"
            type="button"
          >
            <Icon iconName="closeCircle" width={20} height={20} />
          </button>
        )}

        {/* Optional title */}
        {title && (
          <div className="modal__header">
            <h2 id="modal-title" className="modal__title">
              {title}
            </h2>
          </div>
        )}

        {/* Modal content */}
        <div className="modal__content">{children}</div>
      </div>
    </div>
  );
};

export default Modal;
